import {
	FormControl,
	FormHelperText,
	MenuItem,
	Select as SelectMUI,
} from '@mui/material'
import Box from '@mui/material/Box'
import { FC, useEffect } from 'react'
import { Controller, FieldPath, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { FormValues } from '../Calculator'
import { FieldConfig } from '../helpers/config'
import { StyledSelectLabel } from './styles'

interface SelectFieldProps {
	fieldName: FieldPath<FormValues>
	label: string
	dependentFields: { prefix: string; fields: FieldConfig['dependentFields'] }
	options: readonly string[]
}

const Select: FC<SelectFieldProps> = ({
	fieldName,
	label,
	options,
	dependentFields: { prefix, fields },
}: SelectFieldProps) => {
	const {
		control,
		/* eslint-disable @typescript-eslint/no-unused-vars */
		formState: { errors },
		setValue,
		getValues,
		getFieldState,
	} = useFormContext()

	const { t } = useTranslation()

	const hasError = getFieldState(fieldName).invalid

	useEffect(() => {
		if (
			options.length === 1 &&
			options[0] === 'NaN' &&
			getValues(fieldName) !== 'NaN'
		) {
			setValue(fieldName, 'NaN')
		}
	}, [options, fieldName, getValues, setValue])

	const handleResetValues = (arr: FieldConfig['dependentFields']): void => {
		for (const field of fields ?? []) {
			setValue(`${prefix}.${field}`, '')
		}
	}

	return (
		<Box display="flex">
			<Controller
				name={fieldName}
				control={control}
				render={({ field }) => (
					<FormControl
						variant="outlined"
						error={hasError && options.length > 0}
						fullWidth
					>
						<StyledSelectLabel shrink id={`${fieldName}-label`}>
							{label}
						</StyledSelectLabel>

						<SelectMUI
							{...field}
							displayEmpty
							variant="filled"
							labelId={`${fieldName}-label`}
							disabled={options.length === 0 || field.value === 'NaN'}
							fullWidth
							onChange={event => {
								handleResetValues(fields)
								field.onChange(event.target.value)
							}}
							inputRef={field.ref}
						>
							<MenuItem value="" disabled>
								{t('calculator.form.placeholder')}
							</MenuItem>
							{options.map((value: string) => (
								<MenuItem
									key={value.split(' ').join('_').toLowerCase()}
									value={value}
								>
									{value.toLowerCase() === 'nan'
										? t('calculator.form.nan')
										: value}
								</MenuItem>
							))}
						</SelectMUI>
						{hasError && options.length > 0 && (
							<FormHelperText>
								{t('calculator.errors.message_required')}
							</FormHelperText>
						)}
					</FormControl>
				)}
			/>
		</Box>
	)
}

export default Select
